<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.price_template.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="md" type="neutral" @click="openCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.price_template.create") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <!-- Read-only notice: BE requires modern_trade >= 2 for every write. -->
      <div v-if="!isEditModernTrade" class="alert alert-warning mb-4">
        <i class="fa fa-lock mr-1"></i> {{ $t("modern_trade.price_template.read_only") }}
      </div>

      <div class="row">
        <!-- Template list (40%) -->
        <div class="col-12 mt-col-left mb-4">
          <card class="no-border-card" body-classes="px-0 py-0">
            <template slot="header">
              <h3 class="mb-0">{{ $t("modern_trade.price_template.title") }}</h3>
            </template>
            <div v-loading="loadingList">
              <!-- Load failed on page open: no popup, just say so and offer a retry. -->
              <div v-if="listLoadFailed" class="alert alert-warning mx-3 mt-3 mb-0 d-flex align-items-center justify-content-between">
                <span><i class="fa fa-exclamation-circle mr-1"></i> {{ $t("modern_trade.price_template.load_failed") }}</span>
                <base-button size="sm" type="warning" @click="loadList({ silent: false })">
                  <i class="fa fa-redo"></i> {{ $t("modern_trade.price_template.retry") }}
                </base-button>
              </div>
              <el-table
                :data="templates"
                row-key="id"
                header-row-class-name="thead-light"
                highlight-current-row
                :empty-text="templatesEmptyText"
                @row-click="selectTemplate"
              >
                <el-table-column :label="$t('modern_trade.price_template.name')" min-width="140">
                  <template slot-scope="{ row }">
                    <strong :class="{ 'text-primary': selectedId === row.id }">{{ row.name }}</strong>
                    <small v-if="row.status !== 1" class="d-block text-muted">{{ $t("modern_trade.price_template.inactive") }}</small>
                  </template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.price_template.items_count')" width="96" align="center">
                  <template slot-scope="{ row }">
                    <span class="badge badge-secondary">{{ row.item_count !== undefined ? row.item_count : "-" }}</span>
                  </template>
                </el-table-column>
                <el-table-column
                  v-if="isEditModernTrade"
                  :label="$t('actions')"
                  width="72"
                  align="center"
                >
                  <template slot-scope="{ row }">
                    <!-- Single kebab (vertical three-dot) trigger → dropdown of row
                         actions. @click.native.stop keeps the trigger from firing the
                         table's row-click (which would also select the template). -->
                    <el-dropdown trigger="click" @command="cmd => onRowCommand(cmd, row)">
                      <base-button
                        size="sm"
                        type="secondary"
                        class="btn-icon-only"
                        :title="$t('actions')"
                        @click.native.stop
                      >
                        <i class="fa fa-ellipsis-v"></i>
                      </base-button>
                      <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item command="select">
                          <i class="fa fa-money-bill text-success mr-2"></i>{{ $t("modern_trade.price_template.select_for_pricing") }}
                        </el-dropdown-item>
                        <el-dropdown-item command="edit">
                          <i class="fa fa-edit text-info mr-2"></i>{{ $t("modern_trade.price_template.rename") }}
                        </el-dropdown-item>
                        <el-dropdown-item command="delete" divided>
                          <span class="text-danger"><i class="fa fa-trash mr-2"></i>{{ $t("delete") }}</span>
                        </el-dropdown-item>
                      </el-dropdown-menu>
                    </el-dropdown>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </card>
        </div>

        <!-- Items editor (60%) -->
        <div class="col-12 mt-col-right mb-4">
          <card class="no-border-card" body-classes="px-0 pb-1">
            <template slot="header">
              <div class="d-flex align-items-center justify-content-between">
                <h3 class="mb-0">
                  {{ $t("modern_trade.price_template.items") }}
                  <small v-if="selected" class="text-muted">— {{ selected.name }}</small>
                </h3>
                <base-button
                  v-if="selected && isEditModernTrade"
                  size="sm"
                  type="success"
                  :loading="savingItems"
                  @click="saveItems"
                >
                  <i class="fa fa-save"></i> {{ $t("modern_trade.price_template.save_items") }}
                </base-button>
              </div>
            </template>

            <div v-if="!selected" class="text-center text-muted py-5">
              {{ templates.length ? $t("modern_trade.price_template.select_hint") : templatesEmptyText }}
            </div>

            <div v-else v-loading="loadingItems">
              <!-- Product preload failed → inline, retryable. Never a popup on open. -->
              <div v-if="isEditModernTrade && productsLoadFailed" class="alert alert-warning mx-3">
                <strong>{{ $t("modern_trade.price_template.products_load_failed") }}</strong>
                <div class="mt-1">{{ $t("modern_trade.price_template.products_load_failed_hint") }}</div>
                <base-button size="sm" type="warning" class="mt-2" @click="retryLoadProducts">
                  <i class="fa fa-redo"></i> {{ $t("modern_trade.price_template.retry") }}
                </base-button>
              </div>

              <!-- MT master is empty → nothing can be priced; point the user at it. -->
              <div v-if="isEditModernTrade && productsLoaded && !productsLoadFailed && !productOptions.length && !productSearchQuery" class="alert alert-warning mx-3">
                <strong>{{ $t("modern_trade.price_template.no_products") }}</strong>
                <div class="mt-1">{{ $t("modern_trade.price_template.no_products_hint") }}</div>
                <router-link to="/modern-trade/products" class="btn btn-sm btn-warning mt-2">
                  {{ $t("modern_trade.price_template.go_products") }}
                </router-link>
              </div>

              <!-- Add product row -->
              <div v-if="isEditModernTrade" class="row align-items-end px-3 pb-3">
                <div class="col-md-6">
                  <label class="form-control-label">{{ $t("modern_trade.price_template.product") }}</label>
                  <el-select
                    v-model="addProductId"
                    class="w-100"
                    filterable
                    clearable
                    remote
                    reserve-keyword
                    :placeholder="$t('modern_trade.product.search_placeholder')"
                    :remote-method="remoteSearchProducts"
                    :loading="searchingProducts"
                    :no-data-text="productsNoDataText"
                    @visible-change="onProductDropdown"
                  >
                    <el-option
                      v-for="p in productOptions"
                      :key="p.id"
                      :label="`${p.code} - ${p.name}`"
                      :value="p.id"
                    ></el-option>
                  </el-select>
                </div>
                <div class="col-md-3">
                  <label class="form-control-label">{{ $t("modern_trade.price_template.retail_price") }}</label>
                  <input v-model.number="addRetailPrice" class="form-control" type="number" step="0.01" min="0" />
                </div>
                <div class="col-md-3 pb-1">
                  <base-button type="primary" block @click="addItem">
                    <i class="fa fa-plus"></i> {{ $t("modern_trade.price_template.add_item") }}
                  </base-button>
                </div>
              </div>

              <el-table
                :data="items"
                header-row-class-name="thead-light"
                size="small"
                :empty-text="$t('modern_trade.price_template.empty_items')"
              >
                <el-table-column :label="$t('modern_trade.product.code')" width="110">
                  <template slot-scope="{ row }">{{ row.product_code || row.code || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.product.name')" min-width="150" show-overflow-tooltip>
                  <template slot-scope="{ row }">{{ row.product_name || row.name || "-" }}</template>
                </el-table-column>
                <el-table-column :label="$t('modern_trade.price_template.retail_price')" width="130" align="right">
                  <template slot-scope="{ row }">
                    <input
                      v-model.number="row.retail_price"
                      class="form-control form-control-sm text-right"
                      type="number"
                      step="0.01"
                      min="0"
                      :disabled="!isEditModernTrade"
                    />
                  </template>
                </el-table-column>
                <el-table-column v-if="isEditModernTrade" :label="$t('actions')" width="80" align="center">
                  <template slot-scope="{ $index }">
                    <base-button size="sm" type="danger" @click.native="removeItem($index)">
                      <i class="fa fa-trash"></i>
                    </base-button>
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </card>
        </div>
      </div>
    </div>

    <!-- Create / Rename Dialog -->
    <el-dialog
      :title="isEditTpl ? $t('modern_trade.price_template.edit') : $t('modern_trade.price_template.create')"
      :visible.sync="showDialog"
      width="480px"
      @closed="resetForm"
    >
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.price_template.name") }} *</label>
        <input v-model="form.name" class="form-control" type="text" />
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.price_template.note") }}</label>
        <input v-model="form.note" class="form-control" type="text" />
      </div>
      <div class="form-group">
        <label class="form-control-label">{{ $t("modern_trade.price_template.status") }}</label>
        <el-select v-model="form.status" class="w-100">
          <el-option :label="$t('modern_trade.price_template.active')" :value="1"></el-option>
          <el-option :label="$t('modern_trade.price_template.inactive')" :value="0"></el-option>
        </el-select>
      </div>
      <span slot="footer">
        <el-button @click="showDialog = false">{{ $t("close") }}</el-button>
        <el-button type="primary" :loading="saving" @click="handleSave">{{ $t("save") }}</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Dialog, Button, Dropdown, DropdownMenu, DropdownItem } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtPriceTemplates",
  components: {
    RouteBreadCrumb,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Dialog.name]: Dialog,
    [Button.name]: Button,
    [Dropdown.name]: Dropdown,
    [DropdownMenu.name]: DropdownMenu,
    [DropdownItem.name]: DropdownItem,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
    // Explain *why* the product dropdown is empty instead of the generic "No data".
    productsNoDataText() {
      if (this.searchingProducts) return this.$t("loading") || "...";
      if (this.productsLoadFailed) return this.$t("modern_trade.price_template.products_load_failed");
      if (this.productSearchQuery) return this.$t("modern_trade.price_template.no_products_match");
      return this.$t("modern_trade.price_template.no_products");
    },
    // The list is empty for two very different reasons: nothing to show, or the
    // request blew up. Only the first one may say "no templates yet".
    templatesEmptyText() {
      if (this.listLoadFailed) return this.$t("modern_trade.price_template.load_failed");
      return this.$t("modern_trade.price_template.empty_templates");
    },
  },
  data() {
    return {
      loadingList: false,
      listLoadFailed: false,
      productsLoadFailed: false,
      templates: [],
      selectedId: null,
      selected: null,
      // Items editor
      loadingItems: false,
      savingItems: false,
      items: [],
      addProductId: null,
      addRetailPrice: null,
      productOptions: [],
      productsLoaded: false,
      productSearchQuery: "",
      searchingProducts: false,
      // Create / rename dialog
      showDialog: false,
      isEditTpl: false,
      saving: false,
      editId: null,
      form: { name: "", note: "", status: 1 },
    };
  },
  mounted() {
    // Both loads here are implicit (the user only opened the page): failures are
    // reported inline — an error popup on page load is pure noise.
    this.loadList({ silent: true });
    // Preload the MT product master: el-select `remote` only calls remote-method
    // while the user types (element-ui bails out on the first handleQueryChange),
    // so without this the dropdown would always open empty.
    if (this.isEditModernTrade) this.loadProducts("", { silent: true });
  },
  methods: {
    // BE error envelope: { Cause: "..." } (go-core HttpResult).
    errMsg(err) {
      const data = (err && err.response && err.response.data) || {};
      const status = err && err.response && err.response.status;
      if (status === 403) return this.$t("modern_trade.price_template.read_only");
      return data.Cause || data.Msg || (err && err.message) || "Error";
    },
    async loadList({ silent = false } = {}) {
      this.loadingList = true;
      try {
        // BE clamps per_page to 100.
        const res = await ModernTradeService.priceTemplatesList({ page: 1, per_page: 100 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.templates = d.items || [];
        this.listLoadFailed = false;
        // Keep the current selection if it still exists; never auto-select the
        // first template — the right card must stay empty until the user picks
        // one (row-click or the money button in the actions column).
        if (this.selectedId && !this.templates.some(t => t.id === this.selectedId)) {
          this.selectedId = null;
          this.selected = null;
          this.items = [];
        } else if (this.selectedId) {
          // Refresh the header/name after a rename or an item-count change.
          this.selected = this.templates.find(t => t.id === this.selectedId) || this.selected;
        }
      } catch (err) {
        this.templates = [];
        this.listLoadFailed = true;
        // eslint-disable-next-line no-console
        console.error("[MtPriceTemplates] load templates failed:", err);
        if (!silent) swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loadingList = false;
      }
    },
    // Kebab dropdown → route the command string to the matching row action.
    // @command fires only on item click, so row-click selection never double-fires.
    onRowCommand(command, row) {
      if (command === "select") this.selectTemplate(row);
      else if (command === "edit") this.openEdit(row);
      else if (command === "delete") this.handleDelete(row);
    },
    async selectTemplate(row) {
      this.selectedId = row.id;
      this.selected = row;
      this.addProductId = null;
      this.addRetailPrice = null;
      // Lazy preload triggered by picking a row, not by an explicit search →
      // stay quiet on failure, the inline banner already tells the story.
      if (this.isEditModernTrade && !this.productsLoaded) this.loadProducts("", { silent: true });
      await this.loadItems(row.id);
    },
    async loadItems(id) {
      this.loadingItems = true;
      try {
        const res = await ModernTradeService.priceTemplateItems(id);
        const d = res.data && res.data.Data;
        let list = [];
        if (Array.isArray(d)) {
          // Data may be the items array directly, or [{ items: [...] }]
          list = d[0] && d[0].items ? d[0].items : d;
        }
        this.items = (list || []).map(it => ({
          product_id: it.product_id,
          product_code: it.product_code || it.code || "",
          product_name: it.product_name || it.name || "",
          retail_price: it.retail_price !== undefined && it.retail_price !== null ? Number(it.retail_price) : null,
        }));
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.loadingItems = false;
      }
    },
    // Fetch MT products (bo_mt_product). Called on mount / on dropdown open /
    // on every keystroke — not only while typing, as element-ui `remote` does.
    // `silent` = the user never asked for this call (preload), so a failure is
    // surfaced inline (banner + dropdown no-data text) instead of a swal popup.
    async loadProducts(query, { silent = false } = {}) {
      this.productSearchQuery = query || "";
      this.searchingProducts = true;
      try {
        const params = { page: 1, per_page: 50, status: 1 };
        if (query) params.search = query;
        const res = await ModernTradeService.productsList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.productOptions = d.items || [];
        this.productsLoadFailed = false;
        this.productsLoaded = true;
      } catch (err) {
        this.productOptions = [];
        this.productsLoadFailed = true;
        this.productsLoaded = true;
        // eslint-disable-next-line no-console
        console.error("[MtPriceTemplates] load products failed:", err);
        if (!silent) swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.searchingProducts = false;
      }
    },
    // el-select remote-method → the user typed a query themselves, so an error
    // here is worth a popup.
    remoteSearchProducts(query) {
      return this.loadProducts(query, { silent: !query });
    },
    // element-ui never fires remote-method on open → do it ourselves (implicit).
    onProductDropdown(visible) {
      if (visible && !this.productOptions.length && !this.searchingProducts) {
        this.loadProducts("", { silent: true });
      }
    },
    // Explicit "try again" from the inline error banner → user-initiated.
    retryLoadProducts() {
      this.loadProducts("", { silent: false });
    },
    addItem() {
      if (!this.addProductId) {
        swal.fire({ title: this.$t("modern_trade.price_template.product_required"), icon: "warning" });
        return;
      }
      // BE rejects retail_price <= 0 (MtPriceTemplateItemsPut).
      if (this.addRetailPrice === null || this.addRetailPrice === "" || isNaN(Number(this.addRetailPrice)) || Number(this.addRetailPrice) <= 0) {
        swal.fire({ title: this.$t("modern_trade.price_template.price_positive"), icon: "warning" });
        return;
      }
      if (this.items.some(it => it.product_id === this.addProductId)) {
        swal.fire({ title: this.$t("modern_trade.price_template.product_duplicate"), icon: "warning" });
        return;
      }
      const p = this.productOptions.find(o => o.id === this.addProductId) || {};
      this.items.push({
        product_id: this.addProductId,
        product_code: p.code || "",
        product_name: p.name || "",
        retail_price: Number(this.addRetailPrice),
      });
      this.addProductId = null;
      this.addRetailPrice = null;
    },
    removeItem(index) {
      this.items.splice(index, 1);
    },
    async saveItems() {
      // Every row must carry a price > 0 — BE 400s otherwise.
      for (const it of this.items) {
        if (it.retail_price === null || it.retail_price === "" || isNaN(Number(it.retail_price)) || Number(it.retail_price) <= 0) {
          swal.fire({ title: this.$t("modern_trade.price_template.price_positive"), icon: "warning" });
          return;
        }
      }
      this.savingItems = true;
      try {
        const payload = { items: this.items.map(it => ({ product_id: it.product_id, retail_price: Number(it.retail_price) })) };
        await ModernTradeService.priceTemplateItemsUpdate(this.selectedId, payload);
        swal.fire({ title: this.$t("modern_trade.price_template.items_saved"), icon: "success", timer: 1500, showConfirmButton: false });
        // Re-read from BE so the user sees exactly what was persisted.
        await this.loadItems(this.selectedId);
        this.loadList();
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.savingItems = false;
      }
    },
    openCreate() {
      this.isEditTpl = false;
      this.editId = null;
      this.form = { name: "", note: "", status: 1 };
      this.showDialog = true;
    },
    openEdit(row) {
      this.isEditTpl = true;
      this.editId = row.id;
      this.form = { name: row.name || "", note: row.note || "", status: row.status !== undefined ? row.status : 1 };
      this.showDialog = true;
    },
    resetForm() {
      this.form = { name: "", note: "", status: 1 };
    },
    async handleSave() {
      if (!this.form.name) {
        swal.fire({ title: this.$t("modern_trade.price_template.name_required"), icon: "warning" });
        return;
      }
      this.saving = true;
      try {
        if (this.isEditTpl) {
          await ModernTradeService.priceTemplateUpdate(this.editId, this.form);
        } else {
          await ModernTradeService.priceTemplateCreate(this.form);
        }
        swal.fire({ title: this.$t("modern_trade.price_template.saved"), icon: "success", timer: 1500, showConfirmButton: false });
        this.showDialog = false;
        this.loadList();
      } catch (err) {
        swal.fire({ title: this.errMsg(err), icon: "error" });
      } finally {
        this.saving = false;
      }
    },
    async handleDelete(row) {
      const result = await swal.fire({
        title: this.$t("modern_trade.price_template.delete_confirm"),
        text: row.name,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("confirm") || "Confirm",
        cancelButtonText: this.$t("cancel") || "Cancel",
      });
      if (!result.value) return;
      try {
        await ModernTradeService.priceTemplateDelete(row.id);
        if (this.selectedId === row.id) {
          this.selectedId = null;
          this.selected = null;
          this.items = [];
        }
        this.loadList();
      } catch (err) {
        const status = err.response && err.response.status;
        // 409 linked-company guard → cannot delete.
        const title = status === 409 ? this.$t("modern_trade.price_template.has_company_cannot_delete") : this.errMsg(err);
        swal.fire({ title, icon: "error" });
      }
    },
  },
};
</script>

<style scoped>
/* 40 / 60 split on desktop; the columns stack full-width below lg. */
@media (min-width: 992px) {
  .mt-col-left {
    flex: 0 0 40%;
    max-width: 40%;
  }
  .mt-col-right {
    flex: 0 0 60%;
    max-width: 60%;
  }
}
/* Keep table headers on a single line (Element UI defaults to break-all,
   which stacked the Thai count/actions headers vertically). */
::v-deep .el-table th .cell {
  white-space: nowrap;
}
</style>
